/* !
 * @copyright WaterStreet. All rights reserved.
 */

/* eslint-disable max-len */
/* eslint-disable @typescript-eslint/no-explicit-any */

import {
	Component,
	Input,
	OnInit
} from '@angular/core';
import {
	Router
} from '@angular/router';
import {
	EntityInstanceApiService
} from '@api/services/entities/entity-instance.api.service';
import {
	DynamicWizardComponent
} from '@dynamicComponents/dynamic-wizard/dynamic-wizard.component';
import {
	EntityService
} from '@entity/services/entity.service';
import {
	InsuranceConstants
} from '@insurance/constants/insurance-constants';
import {
	InsuranceService
} from '@insurance/services/insurance.service';
import {
	FormlyFieldConfig
} from '@ngx-formly/core/lib/models/fieldconfig';
import {
	AppConstants
} from '@shared/constants/app.constants';
import {
	FormlyConstants
} from '@shared/constants/formly.constants';
import {
	AnyHelper
} from '@shared/helpers/any.helper';
import {
	ObjectHelper
} from '@shared/helpers/object.helper';
import {
	Activity
} from '@shared/implementations/application-data/activity';
import {
	IDynamicComponentContext
} from '@shared/interfaces/application-objects/dynamic-component-context.interface';
import {
	IDynamicComponent
} from '@shared/interfaces/application-objects/dynamic-component.interface';
import {
	IWizardContext
} from '@shared/interfaces/dynamic-interfaces/wizard-context.interface';
import {
	IEntityInstance
} from '@shared/interfaces/entities/entity-instance.interface';
import {
	ActivityService
} from '@shared/services/activity.service';
import {
	ModuleService
} from '@shared/services/module.service';
import {
	SiteLayoutService
} from '@shared/services/site-layout.service';

/* eslint-enable max-len */

@Component({
	selector: 'adjustment-summary',
	templateUrl: './adjustment-summary.component.html',
	styleUrls: [
		'./adjustment-summary.component.scss'
	]
})

/**
 * A component representing a wizard step to display the commissions summary
 * information.
 *
 * @export
 * @class AdjustmentSummaryComponent
 * @implements {OnInit}
 * @implements {IDynamicComponent<DynamicWizardComponent, IWizardContext>}
 */
export class AdjustmentSummaryComponent
implements OnInit, IDynamicComponent<DynamicWizardComponent, IWizardContext>
{
	/**
	 * Initializes an instance of the adjustment write off component.
	 *
	 * @param {Router} router
	 * The router used for navigation and url query parameter storage.
	 * @param {ActivityService} activityService
	 * The activity message service used to notify the user.
	 * @param {ModuleService} moduleService
	 * The module service used to set module changes.
	 * @param {InsuranceService} insuranceService
	 * The insurance service used to lookup insurance modules.
	 * @param {EntityInstanceApiService} entityInstanceApiService
	 * The entity instance api service used in this component.
	 * @param {EntityService} entityService
	 * The entity service api used in this component.
	 * @param {SiteLayoutService} siteLayoutService
	 * The site layout service used in this component.
	 * @memberof AdjustmentSummaryComponent
	 */
	public constructor(
		public router: Router,
		public activityService: ActivityService,
		public moduleService: ModuleService,
		public insuranceService: InsuranceService,
		public entityInstanceApiService: EntityInstanceApiService,
		public entityService: EntityService,
		public siteLayoutService: SiteLayoutService)
	{
	}

	/**
	 * Gets or sets the context of this dynamic component that will be set
	 * during initialization. The source is the content component and
	 * the data will be associated data that we desire to pass explicitly.
	 *
	 * @type {IDynamicComponentContext<
	 * 	DynamicWizardComponent,
	 * 	IWizardContext>}
	 * @memberof AdjustmentSummaryComponent
	 */
	@Input() public context: IDynamicComponentContext<
		DynamicWizardComponent,
		IWizardContext>;

	/**
	 * Gets or sets the formly layout used in implementing components.
	 *
	 * @type {FormlyFieldConfig[]}
	 * @memberof AdjustmentSummaryComponent
	 */
	public dynamicFormlyLayout: FormlyFieldConfig[] =
		<FormlyFieldConfig[]>
		[
			{
				key: 'note',
				type: FormlyConstants.customControls.customTextArea,
				wrappers: [
					FormlyConstants.customControls.customFieldWrapper
				],
				props: {
					label: 'Note',
					rows: FormlyConstants.textAreaRowSizes.small,
					gridColumns: 12,
					required: true
				}
			}
		];

	/**
	 * Gets or sets the context active menu item current data collected on the
	 * dynamic wizard steps.
	 *
	 * @type {any}
	 * @memberof AdjustmentSummaryComponent
	 */
	public currentData: any;

	/**
	 * Gets or sets the agency id associated to the commission adjustment.
	 *
	 * @type {any}
	 * @memberof AdjustmentSummaryComponent
	 */
	public agencyId: number;

	/**
	 * Gets the workflow action being executed to create the commission
	 * adjustment.
	 *
	 * @type {string}
	 * @memberof AdjustmentSummaryComponent
	 */
	public readonly workflowActionName: string =
		'CreateCommissionAdjustment';

	/**
	 * Implements the on initialization interface.
	 *
	 * @async
	 * @memberof AdjustmentSummaryComponent
	 */
	public async ngOnInit(): Promise<void>
	{
		this.currentData =
			this.context.source.activeMenuItem.currentData.data;

		this.agencyId =
			this.currentData.id;

		const ledgerEntityInstance: IEntityInstance =
			await this.insuranceService.getAgencyLedger(
				this.currentData.id);

		const ledgerPreviousBalance: number =
			AnyHelper.isNullOrEmpty(ledgerEntityInstance.data?.balance)
				? 0
				: ledgerEntityInstance.data.balance;

		const adjustmentDetailsData: any =
			{
				priorBalance: ledgerPreviousBalance,
				newBalance:
					ledgerPreviousBalance + this.currentData.amount
			};

		this.currentData =
			{
				...this.currentData,
				adjustmentDetails: adjustmentDetailsData
			};

		this.context.source.addOrUpdateStepData(
			{
				adjustmentDetails: adjustmentDetailsData,
			});

		this.context.source.addToNext(
			this.commissionAdjustment.bind(this));

		this.context.source.wizardStepLoading = false;
	}

	/**
	 * Identifies if transaction number has been provided.
	 *
	 * @memberof AdjustmentSummaryComponent
	 */
	public hasTransactionNumber(): boolean
	{
		return !AnyHelper.isNullOrEmpty(this.currentData.transactionNumber);
	}

	/**
	 * Creates the commission adjustment and navigates
	 * to the agency entity instance.
	 *
	 * @async
	 * @memberof AdjustmentSummaryComponent
	 */
	public async commissionAdjustment(): Promise<void>
	{
		let requestBody: any = null;

		const queryParams: any =
			{
				transactionNumber:
					this.currentData.transactionNumber,
				amount:
					this.currentData.amount,
				commissionTransactionId:
					this.currentData.commissionTransactionId,
				transactionDate:
					this.currentData.transactionDate,
				note:
					this.currentData.note,
				reason:
					this.currentData.reason
			};

		if (!AnyHelper.isNull(
			this.currentData.transactionId)
			&& !AnyHelper.isNullOrEmptyArray(
				this.currentData.paymentAllocations))
		{
			requestBody =
				{
					allocationReferences:
						this.currentData.paymentAllocations
							.map((allocation: any) => ({
								id: allocation.referenceId,
								allocatedAmount: allocation.allocatedAmount
							}))
				};
		}

		const queryString: string =
			this.entityInstanceApiService
				.formUrlParam(
					AppConstants.empty,
					queryParams);

		await this.commissionAdjustmentExecution(
			this.agencyId,
			queryString,
			requestBody);

		this.navigateToAgencyDashboard(
			this.agencyId);
	}

	/**
	 * Handles the validity changed event sent from the child dynamic
	 * formly component. This will update the validity of the form for
	 * action buttons.
	 *
	 * @param {boolean} isValid
	 * The validity of the current displayed step data set.
	 * @memberof AdjustmentSummaryComponent
	 */
	public validityChanged(
		isValid: boolean): void
	{
		this.context.source.validStepChanged(isValid);
	}

	/**
	 * Executes the commission adjustment process, which executes the
	 * CreateCommissionAdjustment workflow service with the collected data from
	 * the commission adjustment wizard, then creates the commission ledger
	 * transaction information.
	 *
	 * @async
	 * @param {number} agencyId
	 * The agency id.
	 * @param {string} queryString
	 * The extra parameters sent as query string parameters.
	 * @param {any} requestBody
	 * The object to pass as the request body.
	 * @memberof AdjustmentSummaryComponent
	 */
	private async commissionAdjustmentExecution(
		agencyId: number,
		queryString: string,
		requestBody: any): Promise<void>
	{
		this.entityInstanceApiService.entityInstanceTypeGroup =
			InsuranceConstants.insuranceEntityTypeGroups
				.agencies;

		await this.activityService.handleActivity(
			new Activity(
				this.entityInstanceApiService
					.executeActionWithPayload(
						agencyId,
						this.workflowActionName,
						requestBody,
						queryString),
				'<strong>Processing commission adjustment</strong>',
				'<strong>Commission adjustment processed</strong>',
				'A commission adjustment has been applied to the agency.',
				'A commission adjustment has not been applied to the agency.'));
	}

	/**
	 * This will navigate to the agency dashboard using the provided agency id.
	 *
	 * @param {number} agencyId
	 * The agency id to navigate to.
	 * @memberof AdjustmentSummaryComponent
	 */
	private navigateToAgencyDashboard(
		agencyId: number): void
	{
		this.context.source.addOrUpdateStepData(
			<object>
			{
				automateVerify: false
			});

		this.router.navigate(
			[
				`${this.moduleService.name}/entities`,
				InsuranceConstants.insuranceEntityTypeGroups.agencies,
				AppConstants.viewTypes.edit,
				agencyId
			],
			{
				queryParams: {
					routeData:
						ObjectHelper.mapRouteData(
							{
								layoutType:
									AppConstants.layoutTypes.full
							})
				}
			});
	}
}